import type { NotificationInfo } from '@lub0v/santa-utils';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Panel, Spinner, Typography } from '@src/components';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { BellIcon } from '@src/components/icons/BellIcon';
import { apiUrlPath } from '@src/routing/urls';
import { useNotifications } from './NotificationsContext';
import './Notifications.css';

export function NotificationsList({ onNavigate }: { onNavigate?: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, loading, error, busy, unread, refresh, markRead } = useNotifications();
  const newestFirst = [...items].sort((a, b) => b.created_at - a.created_at || b.id - a.id);
  const unreadItems = newestFirst.filter((item) => !item.is_viewed);
  const readItems = newestFirst.filter((item) => item.is_viewed);
  function renderNotification(item: NotificationInfo) {
    const date = new Date(item.created_at * 1000);
    return (
      <Panel
        key={item.id}
        as="article"
        padding="medium"
        elevated
        edges={{ pieces: 3 }}
        className={`notifications-item${item.is_viewed ? ' notifications-item--read' : ''}`}
        testId={`notification-${item.id}`}
      >
        <div className="notifications-item__top">
          {!Number.isNaN(date.getTime()) && (
            <time dateTime={date.toISOString()}>
              {new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(date)}
            </time>
          )}
          {!item.is_viewed && (
            <Button
              variant="ghost"
              size="small"
              disabled={busy}
              icon={<CloseIcon />}
              aria-label={t('notifications.readOne')}
              onClick={() => void markRead([item.id])}
              testId={`notification-${item.id}-read`}
            />
          )}
        </div>
        <Typography as="p" role="body/base">
          {item.message}
        </Typography>
        {item.url && item.button && (
          <Button
            variant="secondary"
            size="small"
            disabled={busy}
            testId={`notification-${item.id}-open`}
            onClick={async () => {
              if (!item.is_viewed && !(await markRead([item.id]))) return;
              onNavigate?.();
              navigate(apiUrlPath(item.url));
            }}
          >
            {item.button}
          </Button>
        )}
      </Panel>
    );
  }
  return (
    <div
      className="notifications-list"
      data-testid="notifications-list"
      aria-busy={busy || loading}
    >
      {loading && <Spinner label={t('notifications.loading')} />}
      {error && (
        <Panel padding="small" elevated edges={{ pieces: 3 }}>
          <Typography as="p" role="body/sm">
            {t('notifications.error')}
          </Typography>
          <Button variant="secondary" size="small" onClick={() => void refresh()}>
            {t('notifications.retry')}
          </Button>
        </Panel>
      )}
      {unread > 0 && (
        <Button
          variant="ghost"
          size="small"
          disabled={busy}
          onClick={() =>
            void markRead(items.filter((item) => !item.is_viewed).map((item) => item.id))
          }
          testId="notifications-read-all"
        >
          {t('notifications.readAll')}
        </Button>
      )}
      {!loading && !error && !items.length && (
        <Panel padding="medium" elevated edges={{ pieces: 3 }} className="notifications-empty">
          <BellIcon />
          <Typography as="p" role="body/base">
            {t('notifications.empty')}
          </Typography>
        </Panel>
      )}
      {unreadItems.map(renderNotification)}
      {readItems.length > 0 && (
        <details className="notifications-read" data-testid="notifications-read-section">
          <summary className="notifications-read__divider" data-testid="notifications-read-toggle">
            <span>{t('notifications.readSection', { count: readItems.length })}</span>
            <ChevronDownIcon />
          </summary>
          <div className="notifications-list notifications-read__items">
            {readItems.map(renderNotification)}
          </div>
        </details>
      )}
    </div>
  );
}
